// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import deleteLines from "@bg/App/Handlers/RequestQuote/DeleteLines";
import updateLineInfo from "@bg/App/Handlers/RequestQuote/UpdateLineInfo";
import { CustomQuoteLineModel } from "@bg/App/Services/QuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import ProductAvailability from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import ProductImage from "@bg/Overrides/Components/ProductImage";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import CartLineNotes from "@bg/Overrides/Widgets/Cart/CartLineNotes";
import {
    MyListsDetailsProductListLineStyles,
    productListLineStyles,
} from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getUnitRegularPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: CartModel;
    cartLine: CartLineModel;
    groupName: string;
    qty: number;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    settingsCollection: getSettingsCollection(state),
    selectedGroupLines: state.commonActions["selectedGroupLines"],
});

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    removeCartLine,
    deleteLines,
    updateLineInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const styles: MyListsDetailsProductListLineStyles = {
    ...productListLineStyles,
    notesGridItem: {
        css: css`
            ${productListLineStyles.notesGridItem?.css}
            @media (max-width: 481px) {
                &&& .justify-end {
                    justify-content: flex-start;
                }
            }
        `,
    },
};

const RfqRequestQuoteProductLine: React.FC<Props> = ({
    cart,
    cartLine,
    settingsCollection,
    removeCartLine,
    deleteLines,
    updateLineInfo,
    qty,
    groupName,
    setGroupWishListLineIsSelected,
    selectedGroupLines,
}) => {
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [lineGroups, setLineGroups] = useState<any[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (cartLine && cartLine.properties.groups) {
            setLineGroups(JSON.parse(cartLine.properties.groups));
        }
    }, [cartLine]);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setGroupWishListLineIsSelected({
            wishListLineIds: [cartLine.id],
            groupName,
        });
    };

    const isRestricted = () => {
        return cartLine.isRestricted || !cartLine.isActive;
    };

    const deleteCartLine = () => {
        // if only one group is in the line then its an OOTB delete
        setIsDeleting(true);
        if (
            (lineGroups.length === 1 && lineGroups[0].groupName.toLowerCase() === groupName.toLowerCase()) ||
            isRestricted()
        ) {
            removeCartLine({
                cartLineId: cartLine.id,
                isQuoteLine: true,
                onComplete: (result: { successful: any }) => {
                    if (result.successful) {
                        setIsDeleting(false);
                    }
                },
            } as any);
        } else {
            deleteLines({
                cartId: cart.id,
                originalGroupName: groupName,
                newGroupName: "",
                linesModel: [{ lineId: cartLine.id }],
                reloadCart: true,
                onComplete: result => {
                    if (result.successful) {
                        setIsDeleting(false);
                    }
                },
            });
        }
    };

    const updateLineNotes = (notes: string) => {
        if (!notes || notes === cartLine.notes) {
            return;
        }
        updateLineInfo({
            cartId: cart.id,
            originalGroupName: groupName,
            linesModel: [{ lineId: cartLine.id, qtyOrdered: cartLine.qtyOrdered!, notes, sortOrder: cartLine.line! }],
            reloadCart: true,
            onError: error => {
                toasterContext.addToast({ body: translate(error), messageType: "danger" });
            },
        });
    };

    const onQtyOrderedChanged = (qtyOrdered: number) => {
        if (!qtyOrdered) {
            return;
        }

        dispatch({
            type: "Pages/xRfqRequestQuote/UpdateProductsQty",
            lineModel: {
                lineId: cartLine.id,
                qtyOrdered,
                notes: cartLine.notes,
                sortOrder: cartLine.line!,
                originalQty: cartLine.qtyOrdered,
                originalGroupName: groupName,
            } as CustomQuoteLineModel,
        });
    };

    const { productSettings } = settingsCollection;

    const getItemTotal = () => {
        if (!cartLine.qtyOrdered || !cartLine.pricing) {
            return 0;
        }
        const total =
            Math.round(getUnitRegularPrice(cartLine.pricing!, cartLine.qtyOrdered).price * (qty > 0 ? qty : 1) * 100) /
            100;

        return total;
    };

    if (!cartLine) {
        return null;
    }

    return (
        <StyledWrapper
            {...styles.lineInnerContainer}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.imageGridItem}>
                    <StyledWrapper {...styles.dragGrid}>
                        <Checkbox
                            {...styles.selectedCheckbox}
                            checked={
                                selectedGroupLines?.hasOwnProperty(groupName) &&
                                selectedGroupLines[groupName].indexOf(cartLine.id) > -1
                            }
                            onChange={selectChangeHandler}
                            data-test-selector="selectItem"
                        />
                        <StyledWrapper {...styles.dragIconWrapper}>
                            <Icon {...styles.dragHandleIcon} className="drag-handle" />
                        </StyledWrapper>
                    </StyledWrapper>

                    <ProductImage
                        product={cartLine}
                        extendedStyles={{
                            ...styles.productImageStyles,
                            image: {
                                ...styles.productImageStyles?.image,
                                disableLazyLoad: false,
                                css: css`
                                    img {
                                        height: 100%;
                                        object-fit: contain;
                                    }
                                `,
                            },
                        }}
                    />
                </StyledWrapper>

                <StyledWrapper {...styles.descriptionGridItem}>
                    <ProductDescription
                        product={cartLine}
                        lines={2}
                        lineHeight={22}
                        extendedStyles={styles.productDescriptionStyles}
                    />
                    <StyledWrapper {...styles.partNumbersGridItem}>
                        <ProductPartNumbers
                            productNumber={cartLine?.properties?.sku || cartLine?.erpNumber}
                            customerProductNumber={cartLine.customerName}
                            manufacturerItem={cartLine.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.availabilityGridItem}>
                        {productSettings.showInventoryAvailability && (
                            <ProductAvailability
                                productId={cartLine.productId!}
                                availability={cartLine.availability!}
                                unitOfMeasure={cartLine.unitOfMeasure}
                                trackInventory={cartLine.trackInventory}
                                extendedStyles={styles.productAvailabilityStyles as any}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.rightSectionTop}>
                    <StyledWrapper {...styles.priceAndAvailabilityInnerContainer}>
                        <StyledWrapper {...styles.priceGridItem}>
                            {cartLine.properties["pricingFailed"]?.toLowerCase() !== "true" ? (
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={false}
                                    extendedStyles={styles.productPriceStyles as any}
                                />
                            ) : (
                                <Typography
                                    variant="h6"
                                    as="span"
                                    weight={fontConfig.weight.bold}
                                    css={css`
                                        margin-bottom: 0;
                                        width: 100%;
                                        text-align: center;
                                    `}
                                >
                                    N/A
                                </Typography>
                            )}
                        </StyledWrapper>

                        <StyledWrapper {...styles.uomAndQuantityInnerContainer}>
                            <StyledWrapper {...styles.quantityGridItem}>
                                <CartLineQuantity
                                    cart={cart}
                                    editable={true}
                                    onQtyOrderedChange={onQtyOrderedChanged}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.lineTotal}>
                            <Typography variant="legend">{translate("Total")}</Typography>
                            <Typography {...styles.priceTotal}>
                                {cartLine.properties["pricingFailed"]?.toLowerCase() !== "true" ? (
                                    <LocalizedCurrency amount={getItemTotal()} />
                                ) : (
                                    "N/A"
                                )}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsGridItem} className="one-button">
                        <Button
                            {...styles.deleteLink}
                            onClick={() => setDeleteLineModalIsOpen(true)}
                            data-test-selector="deleteListItem"
                            className="delete-icon-button"
                        >
                            <ButtonIcon src="Delete" size={18} color="secondary" />
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                {cartLine.properties["pricingFailed"]?.toLowerCase() === "true" && (
                    <StyledWrapper>
                        <Typography
                            color="danger"
                            size={fontConfig.fontSize.xSmall}
                            lineHeight={fontConfig.lineHeight.singleLineSmall}
                        >
                            {siteMessage("Custom_Price_FailedToLoad")}
                        </Typography>
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.notesGridItem}>
                    <CartLineNotes
                        cart={cart}
                        editable={true}
                        showLineNotes={true}
                        showAddToList={false}
                        onNotesChange={updateLineNotes}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteCartLine}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqRequestQuoteProductLine);
